/**
 * AuthContext
 *
 * Reuses the SAME JWT auth backend as the Staff Audit Scheduler and TRS
 * (php-open-source-saver/jwt-auth) -- there is no separate auth system for
 * this module, since it's meant to read SSS/TRS data through that one
 * backend. Two ways in, both hitting the existing auth routes and both
 * confirmed against the live backend:
 *   - POST /auth/login        { email, password } -> { access_token, token_type, expires_in, user }
 *   - POST /auth/microsoft    { access_token }     -> { access_token, token_type, expires_in, user }
 *     Field name confirmed as `access_token` (an earlier `id_token` guess
 *     was rejected with a 422 validation error). The VALUE must be MSAL's
 *     ACCESS token (`result.accessToken`), not its id token
 *     (`result.idToken`) -- also confirmed against the live backend, which
 *     rejects an id token with "Invalid or expired Microsoft token." even
 *     though it's a perfectly valid, correctly-signed token (the backend
 *     appears to validate it via a Microsoft Graph call, which only
 *     accepts access tokens).
 *     Matches by azure_oid/email server-side; never auto-creates a user --
 *     same behavior as the SSS login.
 *
 * The token is stored under the same `auth_token` localStorage key
 * src/services/apiClient.js already reads for the Authorization header, and
 * a 401 anywhere in the app is surfaced here via the shared `authEvents`
 * EventTarget for instant logout -- same pattern as SSS's AuthContext.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { authEvents, sssApiClient, WIP_STORAGE_KEYS } from '../services/apiClient.js';
import { AuthContext } from './authContextInstance.js';

/**
 * @typedef {object} AuthUser
 * @property {number|string} id
 * @property {string} displayName
 * @property {string} email
 * @property {string} [role]
 * @property {string} [position]
 * @property {number|string} [branchId]
 * @property {string[]} [divisions]
 * @property {'password'|'microsoft'} authProvider
 */

const USER_STORAGE_KEY = 'auth_user';

/**
 * Normalizes the backend's raw `user` object (keyed `name`, `branch_id`,
 * etc.) into the shape components in this app use.
 * @param {any} rawUser
 * @param {'password'|'microsoft'} authProvider
 * @returns {AuthUser}
 */
function toAuthUser(rawUser, authProvider) {
  return {
    id: rawUser.id,
    displayName: rawUser.name,
    email: rawUser.email,
    role: rawUser.role,
    // Not present on the /auth/login or /auth/microsoft response as
    // observed so far -- included for when/if the backend starts
    // returning it (e.g. joined from the `staff` table), so the topbar's
    // "position below username" line has somewhere to read from without
    // another code change. Falls back to role in the UI when absent.
    position: rawUser.position ?? null,
    branchId: rawUser.branch_id,
    divisions: rawUser.divisions,
    authProvider,
  };
}

/**
 * Extracts the most useful message from a Laravel error response --
 * prefers the first field-specific validation message (422s) over the
 * generic top-level `message`, since "The access token field is required."
 * is far more actionable than "The given data was invalid."
 * @param {any} err
 * @param {string} fallback
 */
function extractErrorMessage(err, fallback) {
  const data = err?.response?.data;
  const firstFieldError = data?.errors ? Object.values(data.errors)[0]?.[0] : null;
  return firstFieldError ?? data?.message ?? fallback;
}

/** @param {{ children: import('react').ReactNode }} props */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(/** @type {AuthUser|null} */ (() => {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  }));
  // No isLoading state: nothing here is async on mount anymore (an
  // earlier version called /auth/me here; removed since that endpoint
  // isn't confirmed to exist -- see the note above). The cached session
  // is trusted synchronously from localStorage on the initial render.
  const isLoading = false;
  const [error, setError] = useState(/** @type {string|null} */ (null));

  const persistSession = useCallback((accessToken, authUser) => {
    localStorage.setItem(WIP_STORAGE_KEYS.TOKEN, accessToken);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(authUser));
    setUser(authUser);
  }, []);

  const clearSession = useCallback(() => {
    localStorage.removeItem(WIP_STORAGE_KEYS.TOKEN);
    localStorage.removeItem(USER_STORAGE_KEY);
    setUser(null);
  }, []);

  /**
   * Email/password login against the existing backend.
   * @param {string} email
   * @param {string} password
   */
  const login = useCallback(
    async (email, password) => {
      setError(null);
      try {
        const { data } = await sssApiClient.post('/auth/login', { email, password });
        persistSession(data.access_token, toAuthUser(data.user, 'password'));
        return true;
      } catch (err) {
        setError(extractErrorMessage(err, 'Invalid email or password.'));
        return false;
      }
    },
    [persistSession]
  );

  /**
   * Finishes Microsoft sign-in by exchanging MSAL's ACCESS token (not the
   * id token -- see the file-level note above) for this app's JWT, via the
   * same /auth/microsoft route SSS uses.
   * @param {string} accessToken MSAL's `result.accessToken`
   */
  const loginWithMicrosoft = useCallback(
    async (accessToken) => {
      setError(null);
      try {
        const { data } = await sssApiClient.post('/auth/microsoft', { access_token: accessToken });
        persistSession(data.access_token, toAuthUser(data.user, 'microsoft'));
        return true;
      } catch (err) {
        setError(
          err?.response?.status === 404
            ? 'No account found for this Microsoft user. Contact your admin to be added first.'
            : extractErrorMessage(err, 'Microsoft sign-in failed.')
        );
        return false;
      }
    },
    [persistSession]
  );

  const logout = useCallback(() => {
    clearSession();
    window.location.assign('/login');
  }, [clearSession]);

  // Instant logout on any 401 from apiClient, matching SSS's authEvents pattern.
  useEffect(() => {
    const handleUnauthorized = () => clearSession();
    authEvents.addEventListener('unauthorized', handleUnauthorized);
    return () => authEvents.removeEventListener('unauthorized', handleUnauthorized);
  }, [clearSession]);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      isLoading,
      error,
      login,
      loginWithMicrosoft,
      logout,
    }),
    [user, isLoading, error, login, loginWithMicrosoft, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
